import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import App from './App.tsx';
import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { logger } from '@/utils/logger';
import './index.css';

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const errorResponse = (error as { response?: { status?: number } }).response;
  return errorResponse?.status;
};

// Create a client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60 * 1000, // 3 minutes (reduced from 5 for fresher data)
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: unknown) => {
        const status = getErrorStatus(error);
        // Don't retry on 401/403 errors
        if (status === 401 || status === 403) {
          return false;
        }
        // Reduce retries from 3 to 2 for faster failure detection
        return failureCount < 2;
      },
      refetchOnWindowFocus: false, // Disable automatic refetch on window focus globally
      refetchOnReconnect: true, // Keep refetch on reconnect for reliability
      // Enable placeholder data globally for instant cache display
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      retry: false,
    },
  },
});

// Make query client globally available for auth service and other utilities
window.queryClient = queryClient;

logger.info('MediaSage Ops Dashboard initializing...');

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AdminAuthProvider>
        <App />
      </AdminAuthProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  </React.StrictMode>,
);



